import { useEffect, useState } from 'react';
import { sharedDb } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { Partner } from '@/types';

export function PartnersPage() {
  const { tenantId } = useAuth();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partner | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      const { data } = await sharedDb()
        .from('partners')
        .select('*')
        .order('display_order')
        .order('name');
      setPartners((data ?? []) as unknown as Partner[]);
      setLoading(false);
    })();
  }, [tenantId, creating, editing]);

  const handleSave = async (partner: Partial<Partner>) => {
    if (!tenantId) return;
    if (partner.id) {
      await sharedDb().from('partners').update(partner).eq('id', partner.id);
    } else {
      await sharedDb().from('partners').insert({ ...partner, tenant_id: tenantId });
    }
    setEditing(null);
    setCreating(false);
  };

  return (
    <div>
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <h1 className="text-sm font-semibold">取引先</h1>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded-md bg-slate-900 px-3 py-1 text-xs font-semibold text-white"
        >
          + 新規
        </button>
      </header>

      {loading ? (
        <div className="p-6 text-sm text-slate-500">読み込み中...</div>
      ) : partners.length === 0 && !creating ? (
        <div className="p-6 text-sm text-slate-500">取引先がまだ登録されていません。</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {partners.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setEditing(p)}
                className="block w-full px-4 py-3 text-left hover:bg-slate-50"
              >
                <div className="text-sm font-medium">{p.name}</div>
                <div className="mt-0.5 text-[10px] text-slate-500">
                  {p.formal_name && <span>{p.formal_name}</span>}
                  {p.line_group_id && <span className="ml-2">LINE接続済</span>}
                  {p.board_customer_id && <span className="ml-2">board接続済</span>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {(editing || creating) && (
        <PartnerEditModal
          {...(editing ? { initial: editing } : {})}
          onSave={handleSave}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

interface ModalProps {
  initial?: Partner;
  onSave: (p: Partial<Partner>) => void | Promise<void>;
  onClose: () => void;
}

function PartnerEditModal({ initial, onSave, onClose }: ModalProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [formalName, setFormalName] = useState(initial?.formal_name ?? '');
  const [lineGroupId, setLineGroupId] = useState(initial?.line_group_id ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [busy, setBusy] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="w-full max-w-md rounded-t-xl bg-white p-4 shadow-xl sm:rounded-xl">
        <h2 className="mb-3 text-sm font-semibold">{initial ? '取引先を編集' : '取引先を新規追加'}</h2>
        <div className="space-y-3">
          <Field label="表示名 *">
            <input value={name} onChange={(e) => setName(e.target.value)} className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </Field>
          <Field label="正式名">
            <input value={formalName ?? ''} onChange={(e) => setFormalName(e.target.value)} className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </Field>
          <Field label="LINEグループID">
            <input value={lineGroupId ?? ''} onChange={(e) => setLineGroupId(e.target.value)} className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono" />
          </Field>
          <Field label="電話">
            <input value={phone ?? ''} onChange={(e) => setPhone(e.target.value)} className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </Field>
        </div>
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm">キャンセル</button>
          <button
            type="button"
            disabled={busy || !name.trim()}
            onClick={async () => {
              setBusy(true);
              await onSave({
                ...(initial?.id ? { id: initial.id } : {}),
                name: name.trim(),
                formal_name: formalName?.trim() || null,
                line_group_id: lineGroupId?.trim() || null,
                phone: phone?.trim() || null,
              } as Partial<Partner>);
              setBusy(false);
            }}
            className="flex-1 rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}
